// makeHtml.js — export engine for CLM Studio.
// exportScenario(format, state) → "interactive" | "static" | "pdf"
// Captures the EXACT scenario shown in Flow Studio (in-scope systems with dragged
// positions, selected story, CPI mode, overlays, maturity) and bakes it into a
// self-contained file that reproduces the demo-studio experience.

import { T, LANES, LANE_COLOR, LANE_Y, LANE_H, GUT, CANVAS_W, CANVAS_H, NODE_W, NODE_H, laneMid } from "./clmConfig.jsx";
import { BO_COLOR, BUSINESS_OBJECTS } from "./clmBusinessObjects.jsx";
import { STORIES, FAM_META } from "./clmStories.jsx";
import { PHASES } from "./clmImplementation.jsx";

/* ---------- small helpers ---------- */
const esc = (s) => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

function triggerDownload(filename, html) {
  try {
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 600);
  } catch (e) { console.error("download failed", e); }
}
function openPrint(html) {
  const w = window.open("", "_blank");
  if (!w) { triggerDownload("CLM_Scenario_print.html", html); return; }
  w.document.open(); w.document.write(html); w.document.close();
  setTimeout(() => { try { w.focus(); w.print(); } catch (e) {} }, 700);
}

/* ---------- geometry (mirrors the React app exactly) ---------- */
function geomCenter(sys, layout) {
  const o = layout && layout[sys.id];
  return o ? { x: o.x, y: o.y } : { x: sys.x, y: laneMid(sys.level) };
}
function edgePathJS(a, b, layout) {
  const A = geomCenter(a, layout), B = geomCenter(b, layout);
  const ah = NODE_H[a.role] || 60, bh = NODE_H[b.role] || 60;
  if (a.level === b.level) {
    const sy = A.y - ah / 2, ey = B.y - bh / 2, dx = B.x - A.x, lift = 24 + Math.abs(dx) * 0.08;
    return `M ${A.x} ${sy} C ${A.x + dx * 0.25} ${sy - lift}, ${B.x - dx * 0.25} ${ey - lift}, ${B.x} ${ey}`;
  }
  const down = B.y > A.y;
  const sy = A.y + (down ? ah / 2 : -ah / 2), ey = B.y + (down ? -bh / 2 : bh / 2), dy = ey - sy;
  return `M ${A.x} ${sy} C ${A.x} ${sy + dy * 0.45}, ${B.x} ${ey - dy * 0.45}, ${B.x} ${ey}`;
}
function resolveStepsJS(story, cpiMode) {
  if (cpiMode) return story.steps;
  const out = [];
  for (const st of story.steps) {
    let f = st.f, t = st.t, note = st.note;
    if (t === "CPI") { t = "ERP"; if (!/SAP/.test(note)) note += " (PLMSI direct to SAP — no CPI.)"; }
    else if (f === "CPI") { f = "ERP"; note = note.replace(/CPI/g, "SAP"); }
    if (f === t) continue;
    out.push({ f, t, bo: st.bo, note });
  }
  const dd = [];
  for (const st of out) { const p = dd[dd.length - 1]; if (p && p.f === st.f && p.t === st.t && p.bo === st.bo) continue; dd.push(st); }
  return dd;
}

/* ---------- scope resolution (accepts Set or array) ---------- */
function asSet(x) { return x instanceof Set ? x : new Set(x || []); }
function scopedSystems(state) {
  const sel = asSet(state.scope && state.scope.systems);
  if (sel.size) return state.systems.filter(s => sel.has(s.id));
  return state.systems;
}
function scopedStories(state) {
  const sel = asSet(state.scope && state.scope.stories);
  if (sel.size) return STORIES.filter(s => sel.has(s.id));
  return STORIES;
}
function scenarioFacts(state) {
  const sys = scopedSystems(state);
  const sto = scopedStories(state);
  const bos = new Set(sto.flatMap(s => s.steps.map(x => x.bo)));
  return { sysCount: sys.length, storyCount: sto.length, boCount: bos.size };
}

/* ============================================================
   STATIC SVG (non-interactive snapshot honoring dragged layout)
   ============================================================ */
function renderStaticSVG(state) {
  const layout = state.layout || {};
  const sys = scopedSystems(state);
  const byId = Object.fromEntries(sys.map(s => [s.id, s]));
  const overlays = state.flow.overlays;
  const activeStory = state.flow.activeId ? STORIES.find(s => s.id === state.flow.activeId) : null;
  const steps = activeStory ? resolveStepsJS(activeStory, state.flow.cpiMode) : [];
  const bx = (byId.CN4S && byId.CPI) ? (geomCenter(byId.CN4S, layout).x + geomCenter(byId.CPI, layout).x) / 2 + 8 : 800;

  let s = `<svg viewBox="0 0 ${CANVAS_W} ${CANVAS_H}" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:auto;display:block">`;
  s += `<defs>`;
  LANES.forEach(L => { s += `<linearGradient id="g-${L.id}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${LANE_COLOR[L.id]}" stop-opacity="0.10"/><stop offset="1" stop-color="${LANE_COLOR[L.id]}" stop-opacity="0.02"/></linearGradient>`; });
  s += `<filter id="glow" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="3" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>`;
  LANES.forEach(L => {
    s += `<rect x="${GUT}" y="${LANE_Y[L.id]}" width="${CANVAS_W - GUT - 16}" height="${LANE_H[L.id]}" fill="url(#g-${L.id})" stroke="${LANE_COLOR[L.id]}30" rx="10"/>`;
    s += `<rect x="${GUT}" y="${LANE_Y[L.id]}" width="4" height="${LANE_H[L.id]}" fill="${LANE_COLOR[L.id]}" opacity="0.55"/>`;
    s += `<text x="22" y="${laneMid(L.id) - 6}" fill="${LANE_COLOR[L.id]}" font-family="Sora" font-weight="800" font-size="26">${L.id}</text>`;
    s += `<text x="22" y="${laneMid(L.id) + 13}" fill="#cfe2f5" font-family="IBM Plex Mono" font-size="10.5" font-weight="600">${esc(L.label)}</text>`;
  });
  const COL = { plmsi: T.plmsi, cpi: T.cpi, boundary: T.boundary, bo: "#5b7693" };
  if (activeStory) {
    steps.forEach(st => {
      const a = byId[st.f], b = byId[st.t]; if (!a || !b) return;
      const c = BO_COLOR[st.bo] || "#5be584";
      s += `<path d="${edgePathJS(a, b, layout)}" fill="none" stroke="${c}" stroke-width="3" stroke-opacity="0.55" stroke-linecap="round"/>`;
    });
    if (state.flow.seqOn) steps.forEach((st, i) => {
      const a = byId[st.f], b = byId[st.t]; if (!a || !b) return;
      const A = geomCenter(a, layout), B = geomCenter(b, layout); const mx = (A.x + B.x) / 2, my = (A.y + B.y) / 2;
      const c = BO_COLOR[st.bo] || "#5be584";
      s += `<g transform="translate(${mx},${my})"><circle r="11" fill="#06101d" stroke="${c}" stroke-width="2"/><text x="0" y="4" text-anchor="middle" fill="${c}" font-family="Sora" font-weight="800" font-size="12">${i + 1}</text></g>`;
    });
  } else {
    const base = [["TC", "CN4T", "plmsi"], ["CN4T", "MES", "plmsi"], ["TC", "AIG", "plmsi"], ["AIG", "CPI", "boundary"], ["MES", "CN4S", "plmsi"], ["CN4S", "CPI", "boundary"], ["CPI", "ERP", "cpi"], ["CPI", "SUP", "cpi"], ["MES", "APS", "bo"], ["APS", "CN4S", "bo"], ["HUB", "MES", "bo"], ["MES", "OEE", "bo"], ["OEE", "OINT", "bo"], ["MES", "OINT", "bo"], ["QM", "OINT", "bo"], ["MES", "EDGE", "bo"], ["MES", "QM", "bo"], ["MES", "SFC", "bo"], ["EDGE", "CNC", "bo"], ["EDGE", "PLC", "bo"], ["EDGE", "IIOT", "bo"], ["PLC", "CONV", "bo"], ["CNC", "PROC", "bo"], ["ROB", "PROC", "bo"], ["IIOT", "PROC", "bo"]];
    base.forEach(([f, t, layer]) => {
      const a = byId[f], b = byId[t]; if (!a || !b) return;
      const lyr = layer === "boundary" ? "cpi" : layer;
      if (lyr !== "bo" && !overlays[lyr]) return;
      s += `<path d="${edgePathJS(a, b, layout)}" fill="none" stroke="${COL[layer]}" stroke-width="${layer === "bo" ? 1.5 : 2.4}" stroke-opacity="${layer === "bo" ? 0.42 : 0.8}" stroke-linecap="round" ${layer === "boundary" ? 'stroke-dasharray="7 6"' : ""}/>`;
    });
  }
  if (overlays.boundary && byId.CN4S && byId.CPI) {
    s += `<line x1="${bx}" y1="54" x2="${bx}" y2="420" stroke="${T.boundary}" stroke-width="2.4" stroke-dasharray="7 6"/>`;
    s += `<text x="${bx - 10}" y="48" fill="${T.plmsi}" text-anchor="end" font-family="IBM Plex Mono" font-size="11" font-weight="600">◄ SIEMENS / PLMSI</text>`;
    s += `<text x="${bx + 10}" y="48" fill="${T.cpi}" font-family="IBM Plex Mono" font-size="11" font-weight="600">SAP / CPI ►</text>`;
  }
  const dimSet = activeStory ? new Set(steps.flatMap(st => [st.f, st.t])) : null;
  sys.forEach(n => {
    const w = NODE_W[n.role] || 120, h = NODE_H[n.role] || 60; const c = geomCenter(n, layout);
    const dim = dimSet && !dimSet.has(n.id);
    s += `<g transform="translate(${c.x},${c.y})" opacity="${dim ? 0.16 : 1}">`;
    if (n.role === "bus") s += `<rect x="${-w / 2}" y="${-h / 2}" width="${w}" height="${h}" rx="${h / 2}" fill="#1a0e08" stroke="${n.color}" stroke-width="2.2" filter="url(#glow)"/>`;
    else if (n.role === "gateway") s += `<polygon points="${-w / 2 + 14},${-h / 2} ${w / 2 - 14},${-h / 2} ${w / 2},0 ${w / 2 - 14},${h / 2} ${-w / 2 + 14},${h / 2} ${-w / 2},0" fill="#0c1a2b" stroke="${n.color}" stroke-width="1.8"/>`;
    else if (n.role === "device") s += `<polygon points="${-w / 2 + 12},${-h / 2} ${w / 2 - 12},${-h / 2} ${w / 2},${-h / 2 + 12} ${w / 2},${h / 2 - 12} ${w / 2 - 12},${h / 2} ${-w / 2 + 12},${h / 2} ${-w / 2},${h / 2 - 12} ${-w / 2},${-h / 2 + 12}" fill="#0c1a2b" stroke="${n.color}" stroke-width="1.7"/>`;
    else if (n.role === "physical") s += `<polygon points="${-w / 2 + 12},${-h / 2} ${w / 2 - 12},${-h / 2} ${w / 2},${h / 2} ${-w / 2},${h / 2}" fill="#0c1a2b" stroke="${n.color}" stroke-width="1.7"/>`;
    else s += `<rect x="${-w / 2}" y="${-h / 2}" width="${w}" height="${h}" rx="${n.role === "hub" ? 12 : 8}" fill="${n.vendor === "SAP" ? "#1a140a" : "#0c1a2b"}" stroke="${n.color}" stroke-width="${n.role === "hub" ? 2.2 : 1.7}" ${n.role === "external" ? 'stroke-dasharray="6 3"' : ""}/>`;
    s += `<text x="0" y="${n.sub ? -2 : 5}" text-anchor="middle" fill="#eaf4ff" font-family="Sora" font-weight="700" font-size="${n.role === "hub" ? 13.5 : 11.5}">${esc(n.name)}</text>`;
    if (n.sub) s += `<text x="0" y="13" text-anchor="middle" fill="#7fa0bd" font-family="Space Grotesk" font-size="9">${esc(n.sub)}</text>`;
    s += `</g>`;
  });
  s += `</svg>`;
  return s;
}

/* ============================================================
   STATIC HTML DOCUMENT  (non-interactive snapshot + tables)
   ============================================================ */
function buildStaticHTML(state, forPrint) {
  const f = scenarioFacts(state);
  const sys = scopedSystems(state);
  const sto = scopedStories(state);
  const m = state.maturity;
  const title = state.title || "CLM Studio — Scenario";
  const activeStory = state.flow.activeId ? STORIES.find(s => s.id === state.flow.activeId) : null;

  const sysRows = LANES.map(L => {
    const items = sys.filter(s => s.level === L.id); if (!items.length) return "";
    return `<tr><td class="lvl" style="color:${LANE_COLOR[L.id]}">${L.id}</td><td>${esc(L.label)}</td><td>${items.map(s => esc(s.name)).join(", ")}</td></tr>`;
  }).join("");

  const storyRows = sto.map(s => {
    const steps = resolveStepsJS(s, state.flow.cpiMode);
    const path = steps.map(st => st.f).concat(steps.length ? steps[steps.length - 1].t : []).join(" → ");
    return `<tr><td class="mono">#${s.sn}</td><td class="mono" style="color:${s.sc}">${esc(s.id)}</td><td>${esc(s.title)}</td><td>${FAM_META[s.fam].name}</td><td class="mono small">${esc(path)}</td></tr>`;
  }).join("");

  const boRows = BUSINESS_OBJECTS.map(b => `<tr><td><span class="sw" style="background:${b.color}"></span>${esc(b.name)}</td><td>${esc(b.dir)}</td><td>${esc(b.sor)}</td><td class="mono small">${esc(b.sap)}</td><td class="mono small" style="color:${T.plmsi}">${esc(b.channel)}</td></tr>`).join("");

  const phaseRows = PHASES.map(p => `<tr><td class="lvl" style="color:${p.color}">${p.id}</td><td>${p.icon} ${esc(p.name)}</td><td>${esc(p.owner)}</td><td class="mono small">${esc(p.dur)}</td><td class="mono">${p.art}</td><td class="small">${esc(p.goal)}</td></tr>`).join("");

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>${esc(title)}</title>
<link href="https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=Space+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap" rel="stylesheet">
<style>
  :root{--bg:#070b12;--panel:#0e1726;--line:rgba(120,170,220,.14);--ink:#e9f1fb;--ink2:#93a8c4;--ink3:#5a708c;--plmsi:${T.plmsi};--cpi:${T.cpi}}
  *{box-sizing:border-box;margin:0;padding:0}
  body{background:${forPrint ? "#fff" : "var(--bg)"};color:${forPrint ? "#0b1220" : "var(--ink)"};font-family:'Space Grotesk',sans-serif;padding:26px;max-width:1320px;margin:0 auto}
  .kick{font-family:'IBM Plex Mono';font-size:10.5px;letter-spacing:.28em;color:var(--plmsi);text-transform:uppercase}
  h1{font-family:'Sora';font-weight:800;font-size:26px;margin:6px 0 2px;color:${forPrint ? "#0b1220" : "var(--ink)"}}
  h2{font-family:'Sora';font-weight:700;font-size:16px;margin:26px 0 10px;color:${forPrint ? "#143" : "var(--ink)"}}
  .meta{font-family:'IBM Plex Mono';font-size:11.5px;color:var(--ink2);margin-bottom:4px}
  .kpis{display:flex;gap:14px;flex-wrap:wrap;margin:16px 0}
  .kpi{border:1px solid var(--line);border-radius:12px;padding:12px 16px;background:${forPrint ? "#f5f8fc" : "var(--panel)"}}
  .kpi b{font-family:'Sora';font-weight:800;font-size:24px;display:block;color:${forPrint ? "#0b1220" : "var(--ink)"}}
  .kpi span{font-size:11px;color:var(--ink2)}
  .diagram{border:1px solid var(--line);border-radius:14px;padding:10px;background:${forPrint ? "#0a1018" : "#070d16"};margin:10px 0}
  table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:8px}
  th{background:${forPrint ? "#e8eef6" : "#10243a"};color:${forPrint ? "#1a2a3a" : "#cfe2f5"};font-family:'IBM Plex Mono';font-size:10px;letter-spacing:.05em;text-transform:uppercase;padding:9px 11px;text-align:left;border-bottom:1px solid var(--line)}
  td{padding:8px 11px;border-bottom:1px solid var(--line);color:${forPrint ? "#26323f" : "var(--ink2)"}}
  td.lvl{font-family:'Sora';font-weight:800}
  .mono{font-family:'IBM Plex Mono'} .small{font-size:11px}
  .sw{display:inline-block;width:11px;height:11px;border-radius:3px;margin-right:7px;vertical-align:-1px}
  .foot{margin-top:24px;padding-top:14px;border-top:1px solid var(--line);font-size:10.5px;color:var(--ink3);font-family:'IBM Plex Mono'}
  @page{size:A3 landscape;margin:12mm}
</style></head><body>
  <div class="kick">Closed-Loop Manufacturing · Teamcenter ⇄ Opcenter ⇄ SAP · ISA-95</div>
  <h1>${esc(title)}</h1>
  <div class="meta">CPI mode: <b>${state.flow.cpiMode ? "CPI middleware" : "PLMSI direct (no CPI)"}</b> · Target maturity: <b>L${m}</b>${activeStory ? ` · Focus story: <b style="color:${activeStory.sc}">#${activeStory.sn} ${esc(activeStory.id)}</b>` : ""}</div>
  <div class="kpis">
    <div class="kpi"><b>${f.sysCount}</b><span>systems in scope</span></div>
    <div class="kpi"><b>${f.storyCount}</b><span>user stories in scope</span></div>
    <div class="kpi"><b>${f.boCount}</b><span>distinct business objects</span></div>
    <div class="kpi"><b>L${m}</b><span>target maturity</span></div>
  </div>
  <div class="diagram">${renderStaticSVG(state)}</div>
  <h2>Systems in scope · by ISA-95 level</h2>
  <table><thead><tr><th>Level</th><th>Layer</th><th>Systems</th></tr></thead><tbody>${sysRows}</tbody></table>
  <h2>User stories in scope · resolved path ${state.flow.cpiMode ? "(via CPI)" : "(PLMSI direct)"}</h2>
  <table><thead><tr><th>#</th><th>BPH</th><th>Title</th><th>Family</th><th>Resolved path</th></tr></thead><tbody>${storyRows}</tbody></table>
  <h2>Business-object register</h2>
  <table><thead><tr><th>Business Object</th><th>Direction</th><th>System of Record</th><th>SAP artifact</th><th>PLMSI channel</th></tr></thead><tbody>${boRows}</tbody></table>
  <h2>Implementation roadmap · 7-phase runbook / WBS</h2>
  <table><thead><tr><th>Phase</th><th>Name</th><th>Owner</th><th>Duration</th><th>Artifacts</th><th>Goal</th></tr></thead><tbody>${phaseRows}</tbody></table>
  <div class="foot">Generated by CLM Studio · static snapshot. Process &amp; structure are authoritative; confirm version-specific Siemens FN4S/FN4T &amp; SAP Integration Suite parameter values against current vendor documentation.</div>
</body></html>`;
}

/* ---------- scenario payload baked into the interactive HTML ---------- */
function buildScenarioPayload(state) {
  const layout = state.layout || {};
  const sys = scopedSystems(state);
  // bake absolute geometry + node meta
  const nodes = {};
  sys.forEach(n => {
    const c = geomCenter(n, layout);
    nodes[n.id] = {
      x: c.x, y: c.y, w: NODE_W[n.role] || 120, h: NODE_H[n.role] || 60,
      role: n.role, label: n.name, sub: n.sub || "", c: n.color, lane: n.level,
      vendor: n.vendor, channel: n.channel,
      tip: `${n.desc || ""}  (${n.vendor} · ISA-95 ${n.level} · ${String(n.channel).toUpperCase()})`,
      x0: n.x, y0: laneMid(n.level),
    };
  });
  const idset = new Set(sys.map(s => s.id));
  const base = [["TC", "CN4T", "plmsi"], ["CN4T", "MES", "plmsi", "BOP"], ["TC", "AIG", "plmsi"], ["AIG", "CPI", "boundary"], ["MES", "CN4S", "plmsi"], ["CN4S", "CPI", "boundary"], ["CPI", "ERP", "cpi", "Production Order"], ["CPI", "SUP", "cpi"], ["MES", "APS", "bo"], ["APS", "CN4S", "bo", "Optimized Plan"], ["HUB", "MES", "bo"], ["MES", "OEE", "bo", "OEE Data"], ["OEE", "OINT", "bo", "OEE Data"], ["MES", "OINT", "bo", "Report"], ["QM", "OINT", "bo"], ["MES", "EDGE", "bo"], ["MES", "QM", "bo"], ["MES", "SFC", "bo", "Work Instructions"], ["EDGE", "CNC", "bo"], ["EDGE", "PLC", "bo"], ["EDGE", "IIOT", "bo"], ["PLC", "CONV", "bo"], ["CNC", "PROC", "bo"], ["ROB", "PROC", "bo"], ["IIOT", "PROC", "bo"]];
  const flows = base.filter(([f, t]) => idset.has(f) && idset.has(t)).map(([f, t, layer, bo]) => ({ f, t, layer, bo: bo || null }));
  const stories = scopedStories(state).map(s => ({ sn: s.sn, id: s.id, fam: s.fam, sc: s.sc, title: s.title, steps: s.steps }));
  const laneGeo = LANES.map(L => ({ id: L.id, label: L.label, sub: L.sub, y: LANE_Y[L.id], h: LANE_H[L.id], c: LANE_COLOR[L.id] }));
  return {
    title: state.title || "CLM Studio — Scenario",
    cpiMode: state.flow.cpiMode, overlays: state.flow.overlays, seqOn: state.flow.seqOn,
    activeId: state.flow.activeId || null, maturity: state.maturity,
    nodes, flows, stories, lanes: laneGeo, bo: BO_COLOR,
    geom: { GUT, CANVAS_W, CANVAS_H }, theme: { plmsi: T.plmsi, cpi: T.cpi, boundary: T.boundary, b2mml: T.b2mml, gold: T.gold },
  };
}

/* ============================================================
   INTERACTIVE_JS — the demo-studio engine, parameterised by the
   baked window.__SCENARIO__. Reproduces drag, story focus, sequence
   badges, JS-driven token animation, CPI toggle, BO filter, auto-play.
   ============================================================ */
const INTERACTIVE_JS = `
(function(){
  var S = window.__SCENARIO__;
  var BO = S.bo, N = S.nodes, FLOWS = S.flows, STORIES = S.stories, LANES = S.lanes;
  var GUT = S.geom.GUT, CW = S.geom.CANVAS_W, CH = S.geom.CANVAS_H;
  var TH = S.theme;
  var cpiMode = S.cpiMode;
  var layer = { plmsi: S.overlays.plmsi, cpi: S.overlays.cpi, boundary: S.overlays.boundary, maturity: !!S.overlays.maturity, seq: S.seqOn !== false, bonames: true };
  var RAF = window.requestAnimationFrame || function(cb){ return setTimeout(function(){cb(Date.now());}, 16); };
  var CAF = window.cancelAnimationFrame || clearTimeout;
  var SVGNS = "http://www.w3.org/2000/svg";
  function E(n,a){ a=a||{}; var e=document.createElementNS(SVGNS,n); for(var k in a) e.setAttribute(k,a[k]); return e; }
  var svg = document.getElementById("svg");

  /* ---- resolveSteps (CPI on/off) ---- */
  function resolveSteps(story){
    if(cpiMode) return story.steps;
    var out=[];
    for(var i=0;i<story.steps.length;i++){
      var st=story.steps[i], f=st.f, t=st.t, note=st.note;
      if(t==="CPI"){ t="ERP"; if(!/SAP/.test(note)) note+=" (PLMSI direct to SAP — no CPI.)"; }
      else if(f==="CPI"){ f="ERP"; note=note.replace(/CPI/g,"SAP"); }
      if(f===t) continue;
      out.push({f:f,t:t,bo:st.bo,note:note});
    }
    var dd=[];
    for(var j=0;j<out.length;j++){ var p=dd[dd.length-1]; if(p&&p.f===out[j].f&&p.t===out[j].t&&p.bo===out[j].bo) continue; dd.push(out[j]); }
    return dd;
  }

  /* ---- lanes ---- */
  var lanesG=document.getElementById("lanes");
  LANES.forEach(function(L){
    lanesG.appendChild(E("rect",{x:GUT,y:L.y,width:CW-GUT-16,height:L.h,fill:"url(#g-"+L.id+")",stroke:L.c+"30",rx:10}));
    lanesG.appendChild(E("rect",{x:GUT,y:L.y,width:4,height:L.h,fill:L.c,opacity:".55"}));
    var t=E("text",{x:22,y:L.y+L.h/2-6,fill:L.c,"font-family":"Sora","font-weight":"800","font-size":"26"});t.textContent=L.id;lanesG.appendChild(t);
    t=E("text",{x:22,y:L.y+L.h/2+13,fill:"#cfe2f5","font-family":"IBM Plex Mono","font-size":"10.5","font-weight":"600"});t.textContent=L.label;lanesG.appendChild(t);
  });

  /* ---- geometry ---- */
  function pathD(a,b){
    var A=N[a],B=N[b];
    if(A.lane===B.lane){
      var sy=A.y-A.h/2, ey=B.y-B.h/2, dx=B.x-A.x, lift=24+Math.abs(dx)*.08;
      return "M "+A.x+" "+sy+" C "+(A.x+dx*.25)+" "+(sy-lift)+", "+(B.x-dx*.25)+" "+(ey-lift)+", "+B.x+" "+ey;
    }
    var down=B.y>A.y, sy2=A.y+(down?A.h/2:-A.h/2), ey2=B.y+(down?-B.h/2:B.h/2), dy=ey2-sy2;
    return "M "+A.x+" "+sy2+" C "+A.x+" "+(sy2+dy*.45)+", "+B.x+" "+(ey2-dy*.45)+", "+B.x+" "+ey2;
  }
  var COL={plmsi:TH.plmsi,cpi:TH.cpi,boundary:TH.boundary,bo:"#5b7693"};

  /* ---- base flows ---- */
  var flowsG=document.getElementById("flows"), flowEls=[];
  FLOWS.forEach(function(fl){
    if(!N[fl.f]||!N[fl.t]) return;
    var lyr=fl.layer==="boundary"?"cpi":fl.layer;
    var p=E("path",{d:pathD(fl.f,fl.t),fill:"none",stroke:COL[fl.layer]||"#5b7693","stroke-width":fl.layer==="bo"?1.5:2.4,"stroke-opacity":fl.layer==="bo"?".42":".8","stroke-linecap":"round","data-layer":lyr});
    if(fl.layer==="boundary") p.setAttribute("stroke-dasharray","7 6");
    flowsG.appendChild(p); flowEls.push({p:p,fl:fl,lyr:lyr});
  });

  /* ---- demarcation ---- */
  var bndG=document.getElementById("boundary");
  function drawBoundary(){
    bndG.innerHTML="";
    if(!N.CN4S||!N.CPI) return;
    var bx=(N.CN4S.x+N.CPI.x)/2+8;
    bndG.appendChild(E("line",{x1:bx,y1:54,x2:bx,y2:420,stroke:TH.boundary,"stroke-width":2.4,"stroke-dasharray":"7 6"}));
    var t=E("text",{x:bx-10,y:48,fill:TH.plmsi,"text-anchor":"end","font-family":"IBM Plex Mono","font-size":"11","font-weight":"600"});t.textContent="\\u25c4 SIEMENS / PLMSI";bndG.appendChild(t);
    t=E("text",{x:bx+10,y:48,fill:TH.cpi,"font-family":"IBM Plex Mono","font-size":"11","font-weight":"600"});t.textContent="SAP / CPI \\u25ba";bndG.appendChild(t);
  }
  drawBoundary();

  /* ---- nodes ---- */
  var nodesG=document.getElementById("nodes"), nodeG={};
  function shapeFor(n){
    var w=n.w,h=n.h,hw=w/2,hh=h/2,body;
    if(n.role==="bus"){ body=E("rect",{x:-hw,y:-hh,width:w,height:h,rx:hh,fill:"#1a0e08",stroke:n.c,"stroke-width":2.2,filter:"url(#glow)"}); }
    else if(n.role==="gateway"){ body=E("polygon",{points:(-hw+14)+","+(-hh)+" "+(hw-14)+","+(-hh)+" "+hw+",0 "+(hw-14)+","+hh+" "+(-hw+14)+","+hh+" "+(-hw)+",0",fill:"#0c1a2b",stroke:n.c,"stroke-width":1.8}); }
    else if(n.role==="device"){ body=E("polygon",{points:(-hw+12)+","+(-hh)+" "+(hw-12)+","+(-hh)+" "+hw+","+(-hh+12)+" "+hw+","+(hh-12)+" "+(hw-12)+","+hh+" "+(-hw+12)+","+hh+" "+(-hw)+","+(hh-12)+" "+(-hw)+","+(-hh+12),fill:"#0c1a2b",stroke:n.c,"stroke-width":1.7}); }
    else if(n.role==="physical"){ body=E("polygon",{points:(-hw+12)+","+(-hh)+" "+(hw-12)+","+(-hh)+" "+hw+","+hh+" "+(-hw)+","+hh,fill:"#0c1a2b",stroke:n.c,"stroke-width":1.7}); }
    else { body=E("rect",{x:-hw,y:-hh,width:w,height:h,rx:n.role==="hub"?12:8,fill:n.vendor==="SAP"?"#1a140a":"#0c1a2b",stroke:n.c,"stroke-width":n.role==="hub"?2.2:1.7}); if(n.role==="external") body.setAttribute("stroke-dasharray","6 3"); }
    return body;
  }
  Object.keys(N).forEach(function(k){
    var n=N[k];
    var g=E("g",{transform:"translate("+n.x+","+n.y+")",class:"node","data-key":k});
    g.appendChild(shapeFor(n));
    var t=E("text",{x:0,y:n.sub?-2:5,"text-anchor":"middle",fill:"#eaf4ff","font-family":"Sora","font-weight":"700","font-size":n.role==="hub"?13.5:11.5});t.textContent=n.label;g.appendChild(t);
    if(n.sub){ t=E("text",{x:0,y:13,"text-anchor":"middle",fill:"#7fa0bd","font-family":"Space Grotesk","font-size":"9"});t.textContent=n.sub;g.appendChild(t); }
    nodesG.appendChild(g); nodeG[k]=g;
  });

  /* ---- BO legend + filter ---- */
  var bolist=document.getElementById("bolist"), boFilter=null;
  Object.keys(BO).forEach(function(name){
    var d=document.createElement("div");d.className="bochip";d.setAttribute("data-bo",name);
    d.innerHTML='<span class="sw" style="background:'+BO[name]+'"></span>'+name;
    d.addEventListener("click",function(){ boFilter=(boFilter===name)?null:name; renderStories(); document.querySelectorAll(".bochip").forEach(function(x){x.classList.toggle("on",x.getAttribute("data-bo")===boFilter);}); });
    bolist.appendChild(d);
  });

  /* ---- stories sidebar ---- */
  var storiesEl=document.getElementById("stories"), famSel="ALL", q="";
  var FAMN={T:"PLM — Teamcenter",O:"MES — Opcenter",K:"Data Collection"};
  function visibleStories(){
    return STORIES.filter(function(s){
      if(famSel!=="ALL"&&s.fam!==famSel) return false;
      if(boFilter&&!s.steps.some(function(st){return st.bo===boFilter;})) return false;
      if(q){ var l=q.toLowerCase(); if(!((""+s.sn).indexOf(l)>=0||s.id.toLowerCase().indexOf(l)>=0||s.title.toLowerCase().indexOf(l)>=0)) return false; }
      return true;
    });
  }
  function renderStories(){
    storiesEl.innerHTML="";
    var items=visibleStories();
    document.getElementById("scount").textContent=items.length+"/"+STORIES.length;
    ["T","O","K"].forEach(function(fam){
      var fi=items.filter(function(s){return s.fam===fam;}); if(!fi.length) return;
      var h=document.createElement("div");h.className="grp";h.textContent=FAMN[fam]+" · "+fi.length;storiesEl.appendChild(h);
      fi.forEach(function(s){
        var d=document.createElement("div");d.className="story"+(activeStory&&activeStory.id===s.id?" active":"");d.style.setProperty("--sc",s.sc);d.setAttribute("data-id",s.id);
        d.innerHTML='<div class="r"><span class="sn" style="background:'+s.sc+'">#'+s.sn+'</span><span class="code" style="color:'+s.sc+'">'+s.id+'</span></div><div class="ti">'+s.title+'</div><div class="mi">'+s.steps.length+' steps</div>';
        d.addEventListener("click",function(){ focusStory(s.id); });
        storiesEl.appendChild(d);
      });
    });
    if(!items.length) storiesEl.innerHTML='<div class="empty">No stories match.</div>';
  }
  document.querySelectorAll(".fam").forEach(function(b){ b.addEventListener("click",function(){ famSel=b.getAttribute("data-f"); document.querySelectorAll(".fam").forEach(function(x){x.classList.toggle("on",x===b);}); renderStories(); }); });
  document.getElementById("q").addEventListener("input",function(e){ q=e.target.value; renderStories(); });

  /* ---- layer toggles ---- */
  function applyLayers(){
    flowEls.forEach(function(fe){
      if(activeStory){ return; }
      var on = (fe.lyr==="bo") || layer[fe.lyr]!==false;
      fe.p.style.display=on?"":"none";
    });
    bndG.style.display=layer.boundary?"":"none";
    document.getElementById("maturity").style.display=layer.maturity?"":"none";
  }
  document.querySelectorAll(".toggle").forEach(function(t){
    t.addEventListener("click",function(){
      var L=t.getAttribute("data-layer");
      if(L==="cpiMode"){ cpiMode=!cpiMode; t.classList.toggle("on",cpiMode); t.querySelector(".lbl").textContent=cpiMode?"CPI middleware":"No CPI (PLMSI direct)"; if(activeStory){ step=0; focusStory(activeStory.id); } return; }
      layer[L]=!layer[L]; t.classList.toggle("on",layer[L]);
      if(L==="seq") renderSeq();
      applyLayers();
    });
  });

  /* ---- maturity overlay ---- */
  var matG=document.getElementById("maturity");
  [[70,350,TH.gold,"Strategic CLM (L3)"],[440,140,TH.cpi,"Event-driven (L4)"],[610,270,TH.plmsi,"Federated (L4+)"]].forEach(function(a){
    matG.appendChild(E("rect",{x:GUT,y:a[0],width:CW-GUT-16,height:a[1],fill:a[2],opacity:".05"}));
    var t=E("text",{x:CW-26,y:a[0]+16,fill:a[2],"text-anchor":"end","font-family":"IBM Plex Mono","font-size":"10.5","font-weight":"600"});t.textContent=a[3];matG.appendChild(t);
  });

  /* ---- tooltips ---- */
  var tip=document.getElementById("tip");
  Object.keys(nodeG).forEach(function(k){
    var n=N[k],g=nodeG[k];
    g.addEventListener("mousemove",function(e){ if(dragging) return; tip.style.opacity="1"; tip.style.left=Math.min(e.clientX+15,window.innerWidth-300)+"px"; tip.style.top=(e.clientY+15)+"px"; tip.innerHTML="<b>"+n.label+"</b> · "+(n.sub||"")+"<br>"+n.tip; });
    g.addEventListener("mouseleave",function(){ tip.style.opacity="0"; });
  });

  /* ---- drag ---- */
  var dragging=null,off={x:0,y:0},moved=false;
  function toSvg(e){ try{ var pt=svg.createSVGPoint(); pt.x=e.clientX; pt.y=e.clientY; return pt.matrixTransform(svg.getScreenCTM().inverse()); }catch(err){ return {x:e.clientX,y:e.clientY}; } }
  function redraw(key){
    flowEls.forEach(function(fe){ if(fe.fl.f===key||fe.fl.t===key) fe.p.setAttribute("d",pathD(fe.fl.f,fe.fl.t)); });
    if(activeStory) drawStoryFlows();
    if(key==="CN4S"||key==="CPI") drawBoundary();
    renderSeq();
  }
  Object.keys(nodeG).forEach(function(k){
    var g=nodeG[k];
    g.addEventListener("pointerdown",function(e){ dragging=k; moved=false; tip.style.opacity="0"; g.classList.add("dragging"); try{g.setPointerCapture(e.pointerId);}catch(x){} var p=toSvg(e); off.x=p.x-N[k].x; off.y=p.y-N[k].y; nodesG.appendChild(g); });
    g.addEventListener("pointermove",function(e){ if(dragging!==k) return; moved=true; var p=toSvg(e); N[k].x=p.x-off.x; N[k].y=p.y-off.y; g.setAttribute("transform","translate("+N[k].x+","+N[k].y+")"); redraw(k); });
    var end=function(){ if(dragging===k){ dragging=null; g.classList.remove("dragging"); } };
    g.addEventListener("pointerup",end); g.addEventListener("pointercancel",end);
  });
  document.getElementById("resetLayout").addEventListener("click",function(){
    Object.keys(N).forEach(function(k){ N[k].x=N[k].x0; N[k].y=N[k].y0; nodeG[k].setAttribute("transform","translate("+N[k].x+","+N[k].y+")"); });
    flowEls.forEach(function(fe){ fe.p.setAttribute("d",pathD(fe.fl.f,fe.fl.t)); });
    drawBoundary(); if(activeStory) drawStoryFlows(); renderSeq();
  });

  /* ---- story focus + sequence + player ---- */
  var activeStory=null, steps=[], step=0, playing=false, rafId=null, auto=false, autoQ=[], autoI=0;
  var seqG=document.getElementById("seqbadges"), tokenG=document.getElementById("token"), demobar=document.getElementById("demobar"), storyFlowG=document.getElementById("storyflows");

  function drawStoryFlows(){
    storyFlowG.innerHTML="";
    steps.forEach(function(st){ if(!N[st.f]||!N[st.t]) return; var c=BO[st.bo]||activeStory.sc; storyFlowG.appendChild(E("path",{d:pathD(st.f,st.t),fill:"none",stroke:c,"stroke-width":3,"stroke-opacity":".5","stroke-linecap":"round"})); });
  }
  function renderSeq(){
    seqG.innerHTML="";
    if(!activeStory||!layer.seq) return;
    steps.forEach(function(st,i){ if(!N[st.f]||!N[st.t]) return; var mx=(N[st.f].x+N[st.t].x)/2, my=(N[st.f].y+N[st.t].y)/2; var c=BO[st.bo]||activeStory.sc;
      var g=E("g",{transform:"translate("+mx+","+my+")"}); g.appendChild(E("circle",{r:11,fill:"#06101d",stroke:c,"stroke-width":2})); var t=E("text",{x:0,y:4,"text-anchor":"middle",fill:c,"font-family":"Sora","font-weight":"800","font-size":"12"});t.textContent=i+1;g.appendChild(t); seqG.appendChild(g);
    });
  }
  function focusStory(id){
    activeStory=STORIES.filter(function(s){return s.id===id;})[0]; if(!activeStory) return;
    steps=resolveSteps(activeStory); step=0;
    var inset={}; steps.forEach(function(st){inset[st.f]=1;inset[st.t]=1;});
    Object.keys(nodeG).forEach(function(k){ nodeG[k].style.opacity=inset[k]?"1":".16"; });
    flowEls.forEach(function(fe){ fe.p.style.display="none"; });
    drawStoryFlows(); renderSeq();
    document.querySelectorAll(".story").forEach(function(el){ el.classList.toggle("active",el.getAttribute("data-id")===id); });
    demobar.classList.add("show"); showStep(0,false); startPlay();
  }
  function clearStory(){
    stopPlay(); activeStory=null; steps=[]; auto=false;
    Object.keys(nodeG).forEach(function(k){ nodeG[k].style.opacity="1"; });
    storyFlowG.innerHTML=""; seqG.innerHTML=""; tokenG.innerHTML="";
    document.querySelectorAll(".story").forEach(function(el){ el.classList.remove("active"); });
    demobar.classList.remove("show"); applyLayers();
    document.getElementById("autoBtn").classList.remove("on");
  }
  document.getElementById("clearStory").addEventListener("click",clearStory);

  function showStep(i,animate){
    if(!activeStory) return;
    var n=steps.length; step=((i%n)+n)%n;
    var st=steps[step], c=BO[st.bo]||activeStory.sc;
    document.getElementById("dseq").textContent=step+1; document.getElementById("dseq").style.background=c;
    document.getElementById("dtitle").innerHTML='<span style="color:'+activeStory.sc+'">#'+activeStory.sn+' '+activeStory.id+'</span> · <span style="color:'+c+'">'+st.bo+'</span> · '+st.f+' \\u2192 '+st.t;
    document.getElementById("ddesc").textContent=st.note;
    document.getElementById("dprog").style.width=((step+1)/n*100)+"%";
    if(animate!==false) animateToken(st,c);
  }
  function animateToken(st,c){
    tokenG.innerHTML="";
    if(!N[st.f]||!N[st.t]) return;
    var tmp=E("path",{d:pathD(st.f,st.t),fill:"none",stroke:"none"}); svg.appendChild(tmp);
    var len=0; try{len=tmp.getTotalLength();}catch(e){len=0;}
    var tok=E("g"), tw=st.bo.length*6.6+18;
    tok.appendChild(E("rect",{x:-tw/2,y:-12,width:tw,height:24,rx:12,fill:c,filter:"url(#glow)"}));
    var t=E("text",{x:0,y:5,"text-anchor":"middle",fill:"#06101d","font-family":"IBM Plex Mono","font-weight":"600","font-size":"11.5"});t.textContent=st.bo;tok.appendChild(t);
    tokenG.appendChild(tok);
    var dur=1500,start=null; CAF(rafId);
    function frame(ts){
      if(start===null)start=ts;
      var k=Math.min((ts-start)/dur,1), e=k<.5?2*k*k:1-Math.pow(-2*k+2,2)/2, pt;
      try{ pt=tmp.getPointAtLength(len*e); }catch(err){ pt={x:N[st.f].x+(N[st.t].x-N[st.f].x)*e,y:N[st.f].y+(N[st.t].y-N[st.f].y)*e}; }
      tok.setAttribute("transform","translate("+pt.x+","+pt.y+")");
      if(k<1){ rafId=RAF(frame); }
      else{ try{svg.removeChild(tmp);}catch(x){} if(playing){ setTimeout(function(){ if(playing){ if(step>=steps.length-1){ if(auto){ nextAuto(); } else { showStep(0,true); } } else { showStep(step+1,true); } } },520); } }
    }
    rafId=RAF(frame);
  }
  function startPlay(){ playing=true; document.getElementById("dplay").textContent="\\u23f8"; showStep(step,true); }
  function stopPlay(){ playing=false; CAF(rafId); document.getElementById("dplay").textContent="\\u25b6"; }
  document.getElementById("dplay").addEventListener("click",function(){ playing?stopPlay():startPlay(); });
  document.getElementById("dnext").addEventListener("click",function(){ stopPlay(); auto=false; showStep(step+1,true); });
  document.getElementById("dprev").addEventListener("click",function(){ stopPlay(); auto=false; showStep(step-1,true); });
  document.getElementById("dstop").addEventListener("click",clearStory);

  /* ---- auto-play all ---- */
  function nextAuto(){ autoI++; if(autoI>=autoQ.length){ clearStory(); return; } focusStory(autoQ[autoI].id); }
  document.getElementById("autoBtn").addEventListener("click",function(){
    if(auto){ clearStory(); return; }
    autoQ=visibleStories(); autoI=0; if(!autoQ.length) return;
    auto=true; this.classList.add("on"); focusStory(autoQ[0].id);
  });

  applyLayers(); renderStories();
  if(S.activeId){ focusStory(S.activeId); }
})();
`;

/* ============================================================
   INTERACTIVE HTML DOCUMENT
   ============================================================ */
function buildInteractiveHTML(state) {
  const payload = buildScenarioPayload(state);
  const title = esc(state.title || "CLM Studio — Scenario");
  const lanesGrad = LANES.map(L => `<linearGradient id="g-${L.id}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${LANE_COLOR[L.id]}" stop-opacity=".10"/><stop offset="1" stop-color="${LANE_COLOR[L.id]}" stop-opacity=".02"/></linearGradient>`).join("");
  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>${title}</title>
<link href="https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=Space+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap" rel="stylesheet">
<style>
  :root{--bg:#070b12;--bg2:#0b1220;--panel:#0e1726;--line:rgba(120,170,220,.14);--ink:#e9f1fb;--ink2:#93a8c4;--ink3:#5a708c;--plmsi:${T.plmsi};--cpi:${T.cpi};--boundary:${T.boundary};--b2mml:${T.b2mml};--gold:${T.gold}}
  *{box-sizing:border-box;margin:0;padding:0} html,body{height:100%}
  body{background:radial-gradient(1100px 560px at 82% -6%,rgba(255,122,69,.10),transparent 60%),radial-gradient(900px 640px at 8% 6%,rgba(34,211,197,.10),transparent 55%),var(--bg);color:var(--ink);font-family:'Space Grotesk',sans-serif;overflow:hidden;display:flex;flex-direction:column}
  header{padding:12px 20px 8px;display:flex;align-items:center;gap:16px;flex-wrap:wrap;border-bottom:1px solid var(--line);background:var(--bg2)}
  .kick{font-family:'IBM Plex Mono';font-size:9.5px;letter-spacing:.28em;color:var(--plmsi);text-transform:uppercase}
  h1{font-family:'Sora';font-weight:800;font-size:17px;line-height:1}
  .rail{display:flex;gap:7px;flex-wrap:wrap;align-items:center}
  .toggle{display:flex;align-items:center;gap:7px;cursor:pointer;user-select:none;background:var(--panel);border:1px solid var(--line);border-radius:24px;padding:5px 11px;font-size:11px;color:var(--ink2);font-family:'IBM Plex Mono';font-weight:600}
  .toggle .sw{width:9px;height:9px;border-radius:50%;background:#33465e}
  .toggle.on{color:var(--ink);border-color:var(--c)} .toggle.on .sw{background:var(--c)}
  .btn{background:var(--panel);border:1px solid var(--line);border-radius:8px;color:var(--ink);padding:6px 11px;font-size:12px;cursor:pointer;font-family:'Space Grotesk';font-weight:600}
  .btn.on{background:var(--b2mml);color:#06101d;border-color:var(--b2mml)}
  .body{flex:1;display:flex;min-height:0}
  .side{width:290px;flex:none;background:var(--bg2);border-right:1px solid var(--line);display:flex;flex-direction:column;min-height:0}
  .side .top{padding:12px 12px 8px}
  .side h2{font-family:'IBM Plex Mono';font-size:10px;letter-spacing:.16em;text-transform:uppercase;color:var(--ink2)}
  #q{width:100%;background:var(--panel);border:1px solid var(--line);border-radius:8px;color:var(--ink);padding:7px 10px;font-size:12px;margin-top:8px;font-family:'Space Grotesk';outline:none}
  .fams{display:flex;gap:5px;margin-top:8px}
  .fam{flex:1;padding:5px;border-radius:7px;font-family:'IBM Plex Mono';font-size:10px;font-weight:600;cursor:pointer;color:var(--ink2);background:var(--panel);border:1px solid var(--line);text-align:center}
  .fam.on{color:#06101d;background:#9fb6cd;border-color:transparent}
  .stories{overflow-y:auto;flex:1;padding:0 10px 12px}
  .grp{font-family:'IBM Plex Mono';font-size:9px;letter-spacing:.12em;color:var(--ink3);text-transform:uppercase;padding:9px 4px 5px}
  .story{background:var(--panel);border:1px solid var(--line);border-left:3px solid var(--sc,#39506b);border-radius:9px;padding:8px 11px;margin-bottom:7px;cursor:pointer}
  .story.active{box-shadow:0 0 0 1px var(--sc);border-color:var(--sc)}
  .story .r{display:flex;gap:6px;align-items:center}
  .story .sn{font-family:'IBM Plex Mono';font-size:9px;font-weight:700;color:#06101d;border-radius:4px;padding:1px 5px}
  .story .code{font-family:'IBM Plex Mono';font-size:9.5px;font-weight:600}
  .story .ti{font-size:12px;font-weight:600;color:var(--ink);margin:4px 0 2px}
  .story .mi{font-family:'IBM Plex Mono';font-size:9px;color:var(--ink3)}
  .empty{color:var(--ink3);font-size:12px;padding:20px;text-align:center}
  .stage{flex:1;position:relative;min-width:0;overflow:hidden}
  svg{display:block;width:100%;height:100%}
  .node{cursor:grab}.node.dragging{cursor:grabbing}
  .bolegend{position:absolute;right:12px;top:12px;background:rgba(8,15,26,.9);border:1px solid var(--line);border-radius:12px;padding:10px 12px;max-width:196px;max-height:62%;overflow-y:auto}
  .bolegend h3{font-family:'IBM Plex Mono';font-size:9px;letter-spacing:.12em;color:var(--ink2);text-transform:uppercase;margin-bottom:7px}
  .bochip{display:flex;align-items:center;gap:7px;font-size:10.5px;color:var(--ink2);padding:3px 6px;border-radius:6px;cursor:pointer;border:1px solid transparent}
  .bochip.on{color:var(--ink);background:rgba(255,255,255,.08);border-color:rgba(255,255,255,.25)}
  .bochip .sw{width:11px;height:11px;border-radius:3px;flex:none}
  .demobar{position:absolute;left:50%;transform:translateX(-50%);bottom:14px;display:none;background:rgba(6,14,24,.95);border:1px solid var(--cpi);border-radius:14px;padding:11px 16px;min-width:520px;max-width:80%;box-shadow:0 18px 50px rgba(0,0,0,.6)}
  .demobar.show{display:block}
  .demobar .row{display:flex;align-items:center;gap:12px}
  .demobar .seq{width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:'Sora';font-weight:800;font-size:14px;color:#04121f;flex:none}
  .demobar .txt{flex:1;min-width:0} .demobar .st{font-size:12.5px;color:var(--ink);font-weight:600} .demobar .sd{font-size:11px;color:var(--ink2);margin-top:2px}
  .demobar .ctl{display:flex;gap:6px} .demobar .ctl button{width:30px;height:30px;border-radius:8px;border:1px solid var(--line);background:var(--panel);color:var(--ink);cursor:pointer}
  .progress{height:3px;background:#16273c;border-radius:2px;margin-top:9px;overflow:hidden}.progress>i{display:block;height:100%;width:0;background:linear-gradient(90deg,var(--plmsi),var(--cpi));transition:width .3s}
  .tip{position:fixed;pointer-events:none;z-index:40;background:#06101d;border:1px solid var(--plmsi);border-radius:10px;padding:9px 12px;font-size:11.5px;max-width:280px;color:var(--ink);opacity:0;transition:opacity .1s}
  .tip b{color:var(--plmsi);font-family:'IBM Plex Mono';font-size:10.5px}
  ::-webkit-scrollbar{width:9px}::-webkit-scrollbar-thumb{background:#1e3047;border-radius:5px}
</style></head><body>
<header>
  <div><div class="kick">CLM Flow Studio · Teamcenter ⇄ Opcenter ⇄ SAP · ISA-95</div><h1>${title}</h1></div>
  <div class="rail">
    <label class="toggle on" data-layer="cpiMode" style="--c:var(--cpi)"><span class="sw"></span><span class="lbl">${state.flow.cpiMode ? "CPI middleware" : "No CPI (PLMSI direct)"}</span></label>
    <label class="toggle on" data-layer="plmsi" style="--c:var(--plmsi)"><span class="sw"></span>PLMSI</label>
    <label class="toggle on" data-layer="cpi" style="--c:var(--cpi)"><span class="sw"></span>CPI</label>
    <label class="toggle on" data-layer="boundary" style="--c:var(--boundary)"><span class="sw"></span>Demarcation</label>
    <label class="toggle" data-layer="maturity" style="--c:var(--gold)"><span class="sw"></span>Maturity</label>
    <label class="toggle on" data-layer="seq" style="--c:var(--b2mml)"><span class="sw"></span>Sequence #</label>
    <button class="btn" id="autoBtn">▶ Auto-play all</button>
    <button class="btn" id="resetLayout">⟲ Reset layout</button>
    <button class="btn" id="clearStory">✕ Clear</button>
  </div>
</header>
<div class="body">
  <aside class="side">
    <div class="top">
      <h2>📖 User Stories · BPH <span id="scount" style="color:var(--plmsi)"></span></h2>
      <input id="q" placeholder="Search serial #, BPH or title…"/>
      <div class="fams"><div class="fam on" data-f="ALL">All</div><div class="fam" data-f="T">T</div><div class="fam" data-f="O">O</div><div class="fam" data-f="K">K</div></div>
    </div>
    <div class="stories" id="stories"></div>
  </aside>
  <div class="stage">
    <svg id="svg" viewBox="0 0 ${CANVAS_W} ${CANVAS_H}" preserveAspectRatio="xMidYMid meet">
      <defs>${lanesGrad}<filter id="glow" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="3" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
      <g id="lanes"></g><g id="maturity" style="display:none"></g><g id="flows"></g><g id="storyflows"></g><g id="boundary"></g><g id="seqbadges"></g><g id="nodes"></g><g id="token"></g>
    </svg>
    <div class="bolegend"><h3>● Business objects · click to filter</h3><div id="bolist"></div></div>
    <div class="demobar" id="demobar">
      <div class="row">
        <div class="seq" id="dseq">1</div>
        <div class="txt"><div class="st" id="dtitle">—</div><div class="sd" id="ddesc">—</div></div>
        <div class="ctl"><button id="dprev">⏮</button><button id="dplay">⏸</button><button id="dnext">⏭</button><button id="dstop">⏹</button></div>
      </div>
      <div class="progress"><i id="dprog"></i></div>
    </div>
  </div>
</div>
<div class="tip" id="tip"></div>
<script>window.__SCENARIO__=${JSON.stringify(payload)};</script>
<script>${INTERACTIVE_JS}</script>
</body></html>`;
}

/* ============================================================
   PUBLIC ENTRY
   ============================================================ */
export function exportScenario(format, state) {
  const safe = (state.title || "CLM_Scenario").replace(/[^a-z0-9]+/gi, "_").slice(0, 50);
  if (format === "interactive") {
    triggerDownload(safe + "_interactive.html", buildInteractiveHTML(state));
  } else if (format === "static") {
    triggerDownload(safe + "_static.html", buildStaticHTML(state, false));
  } else if (format === "pdf") {
    openPrint(buildStaticHTML(state, true));
  }
}
export default exportScenario;
